import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import { useGetList, useTranslate } from 'react-admin';
import type { JobApplication } from './JobApplicationCard';

interface ClosureReason {
  id: number;
  name: string;
  category: string;
  phase: 'pre_hire' | 'post_hire';
  isActive: boolean;
  order: number;
}

interface CloseApplicationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (closureReasonId: number) => void | Promise<void>;
  application: JobApplication;
}

/**
 * Dialog shown when moving an application to a terminal status (Encerrado).
 * Forces the user to select a closure reason, filtered by the application's phase
 * (pre_hire vs post_hire) based on whether the candidate was hired.
 */
export const CloseApplicationDialog = ({
  open,
  onClose,
  onConfirm,
  application,
}: CloseApplicationDialogProps) => {
  const translate = useTranslate();
  const [selectedReasonId, setSelectedReasonId] = useState<number | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const expectedPhase: 'pre_hire' | 'post_hire' =
    application?.outcome === 'hired' ? 'post_hire' : 'pre_hire';

  const { data: closureReasons = [], isLoading } = useGetList<ClosureReason>(
    'job-application-closure-reason',
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'order', order: 'ASC' },
      filter: { isActive_eq: true },
    },
    { enabled: open },
  );

  const filteredReasons = useMemo(
    () => closureReasons.filter((r) => r.phase === expectedPhase),
    [closureReasons, expectedPhase],
  );

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedReasonId('');
      setIsSubmitting(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (selectedReasonId === '' || typeof selectedReasonId !== 'number') return;
    try {
      setIsSubmitting(true);
      await onConfirm(selectedReasonId);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isConfirmDisabled =
    selectedReasonId === '' || isSubmitting || isLoading;

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {translate('resources.job-application.closeApplication.title')}
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary">
            {translate('resources.job-application.closeApplication.body')}
          </Typography>
        </Box>

        <FormControl fullWidth sx={{ mb: 2 }} disabled={isLoading}>
          <InputLabel id="close-application-reason-label">
            {translate('resources.job-application.closeApplication.reasonLabel')}
          </InputLabel>
          <Select
            labelId="close-application-reason-label"
            label={translate(
              'resources.job-application.closeApplication.reasonLabel',
            )}
            value={selectedReasonId}
            onChange={(e) =>
              setSelectedReasonId(
                e.target.value === '' ? '' : Number(e.target.value),
              )
            }
          >
            {filteredReasons.map((reason) => (
              <MenuItem key={reason.id} value={reason.id}>
                {reason.name}
              </MenuItem>
            ))}
          </Select>
          {!isLoading && filteredReasons.length === 0 && (
            <FormHelperText>
              {translate('ra.navigation.no_results', {
                _: 'No options available',
              })}
            </FormHelperText>
          )}
        </FormControl>

        {/* Notes field removed: the BE close/move endpoint does not persist
            notes yet. Re-add with wiring once the API accepts it. */}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {translate('resources.job-application.closeApplication.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isConfirmDisabled}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
        >
          {translate('resources.job-application.closeApplication.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloseApplicationDialog;
